import { inject, Injectable, Injector } from '@angular/core';
import { combineLatest, EMPTY, merge, Observable, of, ReplaySubject, switchMap } from 'rxjs';
import { Customer } from '../submit-customer-dialog/submit-customer-dialog.component';
import {
  createCrmObjectModalProviders,
  CrmCreateCrmObjectModalComponent,
  CrmCreateCrmObjectModalResult,
  CrmFieldService,
  ListObjectTypeDialogData,
  ListObjectTypeDialogResult,
  PageAnalyticsInjectionToken,
  PlatformExtensionFieldIds,
  SelectObjectTypeModalComponent,
  StandardExternalIds,
  tableFiltersInjectionTokenGenerator,
} from '@galaxy/crm/static';
import {
  Association,
  CRMApiService,
  CRMAssociationApiService,
  CrmObject,
  FilterGroupOperator,
  FilterOperator,
  ListAssociationsRequestInterface,
} from '@vendasta/crm';
import { distinctUntilChanged, map, filter, take, shareReplay, catchError, tap, withLatestFrom } from 'rxjs/operators';
import { AccountGroupService, ProjectionFilter } from '@galaxy/account-group';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { MatDialog } from '@angular/material/dialog';
import { VbcApiService } from '@galaxy/vbc';
import { TranslateService } from '@ngx-translate/core';
import { GalaxyFilterChipInjectionToken, GalaxyFilterOperator } from '@vendasta/galaxy/filter/chips';
import { FormControl, TouchedChangeEvent, Validators, ValueChangeEvent } from '@angular/forms';
import { OrderStoreService } from '../../core/orders.service';
import { Order } from '@vendasta/sales-orders';
import { CreateAndAssociateRequest, UserService } from '@vendasta/business-center';

export enum AssociatedUserIDType {
  subjectID,
  userID,
}

@Injectable()
export class AssociateUserToOrderService {
  private companyId$: Observable<string>;
  public customers$: Observable<Customer[]>;

  private readonly associateContact$$ = new ReplaySubject<string>(1);
  private readonly associateContact$ = this.associateContact$$.asObservable();

  private readonly order$: Observable<Order>;
  private readonly fieldService = inject(CrmFieldService);

  //TODO: Check if two instances of form can be controlled by one reference the service
  readonly customersControl = new FormControl<Customer | null>(null, [Validators.required]);

  public isCustomerControlInvalid$: Observable<boolean>;

  constructor(
    private readonly accountGroupService: AccountGroupService,
    private readonly crmAPIService: CRMApiService,
    private readonly crmAssociationApiService: CRMAssociationApiService,
    private readonly snackbarService: SnackbarService,
    private readonly dialog: MatDialog,
    private readonly vbcApiService: VbcApiService,
    private readonly injector: Injector,
    private readonly translate: TranslateService,
    private readonly orderStoreService: OrderStoreService,
    private readonly userService: UserService,
  ) {
    this.order$ = orderStoreService.order$.pipe(distinctUntilChanged());
    this.isCustomerControlInvalid$ = this.customersControl.events.pipe(
      filter((event) => event instanceof TouchedChangeEvent || event instanceof ValueChangeEvent),
      map(() => {
        return this.isCustomerFormInvalid() && this.customersControl.touched;
      }),
      shareReplay({ bufferSize: 1, refCount: true }),
    );

    this.companyId$ = this.order$.pipe(
      switchMap((order) =>
        this.accountGroupService.get(order.businessId, new ProjectionFilter({ accountGroupExternalIdentifiers: true })),
      ),
      map((accountGroup) => accountGroup?.externalIdentifiers['platformCrmCompanyId'] ?? ''),
      shareReplay({ bufferSize: 1, refCount: true }),
    );

    const contacts$ = this.retrieveCRMContactsAssociatedWithAccount$();
    const contactCreated$ = combineLatest([contacts$, this.setupContactCreated$()]).pipe(
      map(([contacts, newContact]) => [...contacts, newContact]),
    );

    this.customers$ = merge(contacts$.pipe(distinctUntilChanged()), contactCreated$.pipe(distinctUntilChanged()));
  }

  retrieveCRMContactsAssociatedWithAccount$(): Observable<Customer[]> {
    const contactAssociations$ = combineLatest([this.order$, this.companyId$]).pipe(
      switchMap(([order, id]) => {
        return this.crmAssociationApiService.listAssociation({
          namespace: order.partnerId,
          pagingOptions: {
            pageSize: 1000,
          },
          filtersV2: {
            operator: FilterGroupOperator.FILTER_GROUP_OPERATOR_AND,
            filters: [
              {
                fieldId: 'to_id',
                operator: FilterOperator.FILTER_OPERATOR_IS,
                values: [
                  {
                    string: id,
                  },
                ],
              },
              {
                fieldId: 'association_type',
                operator: FilterOperator.FILTER_OPERATOR_IS,
                values: [
                  {
                    string: 'Contact-Company',
                  },
                ],
              },
            ],
          },
        } as ListAssociationsRequestInterface);
      }),
      map((resp) => resp?.associations?.map((r) => r.fromId) || []),
    );

    const customers: Observable<Customer[]> = combineLatest([this.order$, contactAssociations$]).pipe(
      switchMap(([order, ids]) => {
        if (ids?.length === 0) {
          return of([]);
        }
        return this.crmAPIService
          .getMultiContact({
            namespace: order.partnerId,
            crmObjectIds: ids,
          })
          .pipe(map((res) => this.convertContactsToCustomers(res.crmObjects)));
      }),
    );

    return combineLatest([this.order$, customers]).pipe(
      //Set the default value of the input field if default billing recipient exists
      tap(([order, customers]) => {
        if (
          order?.customerRecipient?.userId &&
          this.customersControl.value?.userId !== order?.customerRecipient?.userId
        ) {
          customers.forEach((customer) => {
            if (customer?.userId === order?.customerRecipient?.userId) {
              this.customersControl.patchValue(customer, { onlySelf: true });
              this.customersControl.markAsTouched();

              this.customersControl.updateValueAndValidity();
              return;
            }
          });
        }
      }),
      map(([_, customers]) => {
        return customers;
      }),
    );
  }

  private setupContactCreated$(): Observable<Customer> {
    const contactId$ = this.associateContact$.pipe(
      distinctUntilChanged(),
      withLatestFrom(this.companyId$, this.order$),
      filter(([contactId, companyId, order]: [string, string, Order]) => !!contactId && !!companyId && !!order),
      switchMap(([contactId, companyId, order]: [string, string, Order]) => {
        const association = <Association>{
          associationType: 'Contact-Company',
          fromId: contactId,
          toId: companyId,
          tags: [],
        };

        return this.crmAssociationApiService
          .createAssociation({
            namespace: order.partnerId,
            association: association,
          })
          .pipe(
            map(() => contactId),
            catchError(() => {
              this.snackbarService.openErrorSnack('SALES_ORDERS.SUBMIT_ORDER_FOR_APPROVAL.ASSOCIATION_FAILED');
              return of(undefined);
            }),
          );
      }),
      filter((contactId) => !!contactId),
    );

    return combineLatest([this.order$, contactId$]).pipe(
      switchMap(([order, id]) =>
        this.crmAPIService
          .getMultiContact({
            namespace: order.partnerId,
            crmObjectIds: [id],
          })
          .pipe(
            map((res) => res.crmObjects),
            map((contacts) => this.convertContactsToCustomers(contacts)),
            map((customers) => customers[0]),
          ),
      ),
      tap((newCustomer) => {
        this.customersControl.patchValue(newCustomer);
        this.customersControl.updateValueAndValidity();
      }),
    );
  }

  /*
  createVBCUserFromCustomer$ creates a user from the customer email and name of the input field
  userIDType: identifies if the returned value is the subjectID or userID of the created user
   */
  public createVBCUserFromCustomer$(userIDType: AssociatedUserIDType): Observable<Customer> {
    return this.order$.pipe(
      switchMap((order) => {
        if (this.isCustomerFormInvalid()) {
          this.customersControl.markAsTouched();
          return EMPTY;
        }
        const customer = this.customersControl.value;

        return this.userService
          .createAndAssociate({
            email: customer?.email?.toLowerCase(),
            accountGroupId: order.businessId,
            partnerId: order.partnerId,
            firstName: customer?.firstName,
            lastName: customer?.lastName,
            sendWelcomeEmail: true,
            failOnExistingUser: false,
            failOnExistingAssociation: false,
          } as CreateAndAssociateRequest)
          .pipe(
            filter((user) => !!user?.userId),
            switchMap((user) => {
              if (userIDType === AssociatedUserIDType.subjectID) {
                return of(user?.userId);
              } else if (userIDType === AssociatedUserIDType.userID) {
                return of(user?.unifiedUserId);
              }
            }),
          );
      }),
      map((userId) => {
        const customer = this.customersControl.value;
        const updatedCustomer: Customer = JSON.parse(JSON.stringify(customer));
        updatedCustomer.userId = userId;

        return updatedCustomer;
      }),
    );
  }

  private openAssociateContactModal(): void {
    const dialogRef = this.dialog.open(SelectObjectTypeModalComponent, {
      data: {
        baseColumnIds: [],
        modalTitle: this.translate.instant(
          'SALES_ORDERS.SUBMIT_ORDER_FOR_APPROVAL.ASSOCIATE_RECIPIENT_WITH_OWNERSHIP_ENTITY',
        ),
        objectType: 'Contact',
        associations: [],
        hideCreateButton: false,
        enableMultiplePreSelectedRows: false,
        singleSelection: true,
        filters: [
          {
            fieldId: this.fieldService.getFieldId(StandardExternalIds.Email),
            filterId: 'filterContactByEmail',
            operator: GalaxyFilterOperator.FILTER_OPERATOR_IS_NOT_EMPTY,
          },
        ],
      } as ListObjectTypeDialogData,
      injector: Injector.create({
        providers: [
          {
            provide: PageAnalyticsInjectionToken,
            useFactory: () => {
              const deps = {
                trackEvent(): void {
                  return;
                },
              };
              return deps;
            },
          },
          {
            provide: GalaxyFilterChipInjectionToken,
            useFactory: tableFiltersInjectionTokenGenerator('Contact'),
          },
        ],
        parent: this.injector,
      }),
    });

    dialogRef
      .afterClosed()
      .pipe(take(1))
      .subscribe((result: ListObjectTypeDialogResult) => {
        if (result?.clickedCreate) {
          return this.openCreateContactModal();
        }

        if (result?.selectedRows.length > 0) {
          const id = result.selectedRows[0].id;
          this.associateContact$$.next(id);
        }
      });
  }

  private openCreateContactModal(): void {
    const ref = this.dialog.open(CrmCreateCrmObjectModalComponent, {
      data: {
        objectType: 'Contact',
      },
      injector: createCrmObjectModalProviders('Contact', this.injector),
      width: '500px',
    });
    ref
      .afterClosed()
      .pipe(take(1))
      .subscribe({
        next: (contact: CrmCreateCrmObjectModalResult) => {
          this.associateContact$$.next(contact.crmObject.crmObjectId);
        },
      });
  }

  public addCRMContact(): void {
    this.openAssociateContactModal();
  }

  public isCustomerFormInvalid() {
    return this.customersControl.invalid || !this.customersControl.value?.email;
  }

  private convertContactsToCustomers(contacts: CrmObject[]): Customer[] {
    return contacts
      .filter((c) => JSON.stringify(c) !== '{}')
      .map((contact: CrmObject): Customer => {
        const userId =
          contact?.fields?.find((field) => field.fieldId === PlatformExtensionFieldIds.UserID)?.stringValue ?? '';
        const firstName =
          contact?.fields?.find((field) => field.externalId === StandardExternalIds.FirstName)?.stringValue ?? '';
        const lastName =
          contact?.fields?.find((field) => field.externalId === StandardExternalIds.LastName)?.stringValue ?? '';
        const email =
          contact?.fields?.find((field) => field.externalId === StandardExternalIds.Email)?.stringValue ?? '';

        return {
          userId: userId,
          firstName: firstName,
          lastName: lastName,
          email: email,
        };
      });
  }
}
